/**
 * Shared worksheet renderer – single source of truth for the printed /
 * PDF worksheet and its answer key.
 *
 * Loaded in the browser as window.WorksheetRender (plain <script>)
 * and require()'d by routes/export.js in Node (UMD, no DOM usage –
 * pure string building only).
 */
(function (root, factory) {
    if (typeof module === 'object' && module.exports) module.exports = factory();
    else root.WorksheetRender = factory();
}(typeof self !== 'undefined' ? self : this, function () {
    'use strict';

    // Same resolution booklet-render.js uses for its schema: require() in Node,
    // the global the <script> tag left behind in the browser.
    const BrandMark = (typeof module === 'object' && module.exports)
        ? require('./brand-mark.js')
        : (typeof window !== 'undefined' ? window.BrandMark : null);

    // Same resolution again: the home-language table is shared with the
    // generator and the composer, so the printed sheet and the on-screen
    // editor cannot drift apart on what a section instruction says in Urdu.
    const HomeLanguages = (typeof module === 'object' && module.exports)
        ? require('./home-languages.js')
        : (typeof window !== 'undefined' ? window.HomeLanguages : null);

    /**
     * A translated twin for a line of page furniture.
     *
     * `dir` and `lang` are set per line rather than on the page: the sheet is
     * an English document with translated asides in it, so an RTL home
     * language has to flip its own line and nothing else. Without `lang` a PDF
     * renderer picks a font by guessing at the codepoints, which is how
     * Chinese ends up in a Japanese face.
     */
    function l1Label(language, key) {
        const lang = HomeLanguages && HomeLanguages.getLanguage(language);
        const text = lang && HomeLanguages.uiString(language, key);
        if (!text) return '';
        return ` <span class="ws-l1-label" lang="${lang.code}" dir="${lang.dir}">${esc(text)}</span>`;
    }

    function l1Line(language, text, cls) {
        const lang = HomeLanguages && HomeLanguages.getLanguage(language);
        if (!lang || !text) return '';
        return `<p class="${cls}" lang="${lang.code}" dir="${lang.dir}">${esc(text)}</p>`;
    }

    // Curve diagrams are drawn by their own module, shared with every other
    // surface that shows one - this file only decides which version to print.
    const CurveDiagram = (typeof module === 'object' && module.exports)
        ? require('./curve-diagram.js')
        : (typeof window !== 'undefined' ? window.CurveDiagram : null);

    const WS_SECTION_LABELS = {
        multiple_choice:   'Multiple Choice',
        true_false:        'True or False',
        fill_blank:        'Fill in the Blank',
        short_answer:      'Short Answer',
        matching:          'Matching',
        extended_response: 'Extended Response',
        whiteboard:        'Whiteboard',
    };

    const WS_SECTION_INSTRUCTIONS = {
        multiple_choice:   'Circle the letter of the correct answer.',
        true_false:        'Circle True or False for each statement.',
        fill_blank:        'Complete each sentence with the correct word or phrase.',
        short_answer:      'Answer each question in full sentences.',
        matching:          'Write the letter of the matching item in the box.',
        extended_response: 'Answer in full, developed paragraphs. Marks are shown for each question.',
        whiteboard:        'Draw or write your answer on the whiteboard, showing your working.',
    };

    // On paper there is no whiteboard – point students at the printed box.
    const WS_PRINT_INSTRUCTION_OVERRIDES = {
        whiteboard: 'Draw or write your answer in the box, showing your working.',
    };

    function esc(str) {
        return String(str == null ? '' : str)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }

    function escAttr(str) {
        return esc(str).replace(/"/g, '&quot;');
    }

    // ── Edit mode ─────────────────────────────────────────
    //
    // The worksheet editor renders this same document and types directly into
    // it, so every string that comes off the model is wrapped in a span
    // carrying the JSON path it was read from - an edit writes back through
    // that path and the DOM is thrown away. A module-level flag rather than a
    // parameter threaded through twenty renderers: this file is synchronous,
    // single-pass string building with no reentrancy, so a flag set for the
    // duration of one buildWorksheetHtml call is the whole of the state.
    //
    // Null in every print and PDF render, which is what keeps that output
    // byte-for-byte what it was before the editor existed.
    let EDIT = null;

    /**
     * The editable wrapper for one model-backed string.
     *
     * `path` is dotted, indices included - 'sections.0.questions.2.question'.
     * `ph` is the placeholder shown when the field is empty; without it a
     * blank new question collapses to nothing and cannot be clicked into.
     */
    function ed(path, str, cls, ph) {
        const html = mathText(str);
        if (!EDIT) return html;
        return `<span class="ws-ed${cls ? ' ' + cls : ''}" contenteditable="true" role="textbox"`
            + ` data-ws-path="${escAttr(path)}"`
            + (ph ? ` data-ws-ph="${escAttr(ph)}"` : '')
            + `>${html}</span>`;
    }

    // ── Tables inside question text ───────────────────────────
    //
    // A question has no table field, so a table it needs is carried as markdown
    // pipes in its text (booklet-schema.js reads them, for booklets too). Drawn
    // here as the table it describes. In the editor the field holds the raw
    // text: app.js swaps it back to its pipes while it has focus, so what is
    // typed is still the one stored string.
    const pipeSchema = () => ((typeof module === 'object' && module.exports)
        ? require('./booklet-schema.js')
        : (typeof window !== 'undefined' ? window.BookletSchema : null));

    function hasTable(str) {
        const S = pipeSchema();
        return !!(S && S.hasPipeTable && str && String(str).includes('|') && S.hasPipeTable(str));
    }

    /** Text with its pipe tables drawn as tables. */
    function tableText(str) {
        const S = pipeSchema();
        if (!S || !S.pipeTableSegments) return mathText(str);
        return S.pipeTableSegments(str).map((seg) => {
            if (seg.type === 'text') return `<span class="ws-q-seg">${mathText(seg.text)}</span>`;
            const [head, ...rows] = seg.rows;
            return `<span class="ws-q-table-wrap" contenteditable="false"><table class="ws-q-table">`
                + `<thead><tr>${head.map((c) => `<th>${mathText(c)}</th>`).join('')}</tr></thead>`
                + `<tbody>${rows.map((r) => `<tr>${r.map((c) => `<td>${mathText(c)}</td>`).join('')}</tr>`).join('')}</tbody></table></span>`;
        }).join('');
    }

    /** A question's numbered stem; a block rather than a <p> when it holds a table. */
    function stemHtml(n, path, str, tail = '') {
        if (!hasTable(str)) return `<p class="ws-q-text"><span class="ws-q-n">${n}</span>${ed(path, str, '', 'Question')}${tail}</p>`;
        const body = EDIT
            ? `<div class="ws-ed ws-ed-table" contenteditable="true" role="textbox" data-ws-path="${escAttr(path)}" data-ws-ph="Question" data-ws-raw="${escAttr(str)}">${tableText(str)}</div>`
            : `<div>${tableText(str)}</div>`;
        return `<div class="ws-q-text ws-q-has-table"><span class="ws-q-n">${n}</span>${body}${tail}</div>`;
    }

    /** Same, for text that must not run through LaTeX rendering (code, translations). */
    function edPlain(path, str, cls, ph) {
        const html = esc(str);
        if (!EDIT) return html;
        return `<span class="ws-ed${cls ? ' ' + cls : ''}" contenteditable="true" role="textbox"`
            + ` data-ws-path="${escAttr(path)}"`
            + (ph ? ` data-ws-ph="${escAttr(ph)}"` : '')
            + `>${html}</span>`;
    }

    /**
     * One structural-action button. Real buttons in DOM order, so Tab reaches
     * them.
     *
     * data-ws-target, not data-ws-path: a button points at the thing it acts
     * on, which is a question, a section or an array, while data-ws-path means
     * one editable string and nothing else. Sharing the name would make
     * "every path is a field you can type into" untrue, and that invariant is
     * what the editor's write-back relies on.
     */
    function edBtn(act, target, label, glyph) {
        return `<button type="button" class="ws-ed-btn" data-ws-act="${escAttr(act)}"`
            + ` data-ws-target="${escAttr(target)}" title="${escAttr(label)}" aria-label="${escAttr(label)}">${glyph}</button>`;
    }

    /**
     * A drag handle: pick a question or a matching pair up by it and drop it
     * somewhere else in the same list. Always visible, in the left margin,
     * because the arrows beside it only appear on hover and were being missed.
     * `kind` is 'q' or 'pair'; `path` is the thing that moves.
     */
    function edGrip(kind, path, label) {
        return `<span class="ws-ed-grip" draggable="true" contenteditable="false" data-ws-drag="${kind}"`
            + ` data-ws-target="${escAttr(path)}" title="${escAttr(label)}" aria-hidden="true">&#8942;&#8942;</span>`;
    }

    // The toolbar icons: drawn, not text glyphs, so they are the same size and
    // weight whatever font the sheet is in.
    const svg = d => `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${d}</svg>`;
    const ICONS = {
        up: svg('<path d="M12 19V5"/><path d="m5 12 7-7 7 7"/>'),
        down: svg('<path d="M12 5v14"/><path d="m19 12-7 7-7-7"/>'),
        copy: svg('<rect x="9" y="9" width="11" height="11" rx="2"/><path d="M5 15V6a2 2 0 0 1 2-2h9"/>'),
        bin: svg('<path d="M4 7h16"/><path d="M10 11v6M14 11v6"/><path d="M6 7l1 13h10l1-13"/><path d="M9 7V4h6v3"/>'),
    };

    /**
     * The hover/focus toolbar on a question. Empty string outside edit mode.
     * Steps and a sentence starter are added from here; once there is one,
     * its text is edited on the sheet like any other.
     */
    function edQuestionTools(qPath, q, type) {
        if (!EDIT) return '';
        const written = (type === 'short_answer' || type === 'extended_response') && !(q && q.parts && q.parts.length);
        const add = (q && !(Array.isArray(q.steps) && q.steps.length) ? edBtn('q-steps', qPath, 'Add numbered steps', '+ Steps') : '')
            + (written && typeof q.starter !== 'string' ? edBtn('q-starter', qPath, 'Add a sentence starter', '+ Starter') : '');
        return edGrip('q', qPath, 'Drag to move this question')
            + `<div class="ws-ed-qtools" contenteditable="false">`
            + (add ? `<span class="ws-ed-qtools-add">${add}</span>` : '')
            + edBtn('q-up', qPath, 'Move up', ICONS.up)
            + edBtn('q-down', qPath, 'Move down', ICONS.down)
            + edBtn('q-dup', qPath, 'Duplicate', ICONS.copy)
            + edBtn('q-del', qPath, 'Delete question', ICONS.bin)
            + `</div>`;
    }

    /**
     * A layout choice the teacher switches on the sheet itself: a labelled
     * button that shows whether it is on. Unlike the hover tools these are
     * always visible in the editor, because a choice nobody can see is a
     * feature nobody finds.
     */
    function edOption(act, target, label, on, value) {
        return `<button type="button" class="ws-ed-btn ws-ed-opt" data-ws-act="${escAttr(act)}"`
            + ` data-ws-target="${escAttr(target)}"${value ? ` data-ws-type="${escAttr(value)}"` : ''}`
            + ` aria-pressed="${on ? 'true' : 'false'}">${esc(label)}</button>`;
    }

    // ── Layout choices stored on the document ─────────────
    // All of these are read off data_json, so a sheet prints the way it was left
    // whoever prints it and whenever.

    // How much of the top of the page is printed. Pupils read a name line and
    // a score box as a test, which is the wrong signal for classwork.
    const WS_HEADERS = ['full', 'title', 'none'];
    function headerMode(data) {
        return data && WS_HEADERS.includes(data.header) ? data.header : 'full';
    }

    // How the sheet looks. Classic is the plain one every sheet had before
    // there was a choice; Bright is for younger pupils and Calm for anyone a
    // busy page puts off. Stored on the document, like the header, so the
    // PDF, the print popup and the pupil's copy in the portal all agree.
    //
    // Each entry is what the editor's Look gallery shows: a name, who it is
    // for, and the few colours its miniature is drawn in (badges are the
    // section letters, in turn). A new look is an entry here and its rules
    // under Themes in worksheet-print.css; the gallery builds itself.
    const WS_THEME_INFO = [
        { id: 'classic', name: 'Classic', blurb: 'Clean and plain. Suits secondary and exam practice.',
            mini: { ink: '#1a1d26', band: 'transparent', rule: '#3660ff', badges: ['#3660ff', '#3660ff', '#3660ff'], font: 'Arial, Helvetica, sans-serif', round: false } },
        { id: 'bright', name: 'Bright', blurb: 'Big, rounded and colourful, for primary pupils.',
            mini: { ink: '#1a3a63', band: '#eef6fd', rule: 'transparent', badges: ['#2563eb', '#f59e0b', '#16a34a'], font: "'Fredoka', 'Nunito', sans-serif", round: true } },
        { id: 'calm', name: 'Calm', blurb: 'One quiet colour and more space, for anyone a busy page puts off.',
            mini: { ink: '#22302e', band: 'transparent', rule: '#b3c4c0', badges: ['#cfe3df', '#cfe3df', '#cfe3df'], font: "'Lexend', Verdana, sans-serif", round: true } },
    ];
    const WS_THEMES = WS_THEME_INFO.map(t => t.id);
    function themeOf(data) {
        return data && WS_THEMES.includes(data.theme) ? data.theme : 'classic';
    }

    // A matching section either has pupils write a letter in a box, or draw a
    // line between the two columns.
    const MATCH_LINES_INSTRUCTION = 'Draw a line from each item on the left to its match on the right.';
    function matchByLines(section) {
        return !!section && section.type === 'matching' && section.matchStyle === 'lines';
    }

    /**
     * The words a fill-in-the-blank section's gaps take, for the word bank.
     * Alphabetical, so the order gives nothing away, and each word once. A
     * supported sheet adds a few wrong words (wordBankExtras), so the last gap
     * is not filled by elimination.
     */
    function wordBankWords(section) {
        if (!section || section.type !== 'fill_blank' || !section.wordBank) return [];
        const seen = new Set();
        const extras = Array.isArray(section.wordBankExtras) ? section.wordBankExtras : [];
        return (section.questions || [])
            .map(q => (q && typeof q.answer === 'string' ? q.answer.trim() : ''))
            .concat(extras.map(w => (typeof w === 'string' ? w.trim() : '')))
            .filter(w => w && !seen.has(w.toLowerCase()) && seen.add(w.toLowerCase()))
            .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    }

    /**
     * The steps every question in a section shares, or null.
     *
     * The `chunked` support writes numbered steps per question. Within one
     * section those are usually the same three lines each time, and printing
     * them under every question buries the questions. When every question
     * carries the same steps they are printed once, under the section's
     * instruction, instead.
     */
    function sharedSteps(section) {
        const qs = (section && section.questions) || [];
        if (qs.length < 2) return null;
        const norm = q => (Array.isArray(q && q.steps) ? q.steps : [])
            .filter(st => typeof st === 'string')
            .map(st => st.trim().toLowerCase());
        const first = norm(qs[0]);
        if (!first.some(Boolean)) return null;
        const key = JSON.stringify(first);
        return qs.every(q => JSON.stringify(norm(q)) === key) ? qs[0].steps : null;
    }

    // Same UMD environment split as the module wrapper itself: Node gets the
    // katex npm package, the browser gets the already-loaded window.katex
    // global (public/vendor/katex/katex.min.js) - this file stays pure
    // string-building either way, no DOM dependency.
    //
    // The browser's is looked up each time it is wanted, not once here: the
    // main app fetches katex after this file, once someone is signed in
    // (loadKatex in app.js), and a reference taken now would be null for good.
    const nodeKatex = (typeof module === 'object' && module.exports) ? require('katex') : null;
    const katexLib = () => nodeKatex || (typeof window !== 'undefined' && window.katex) || null;

    const MATH_REGEX = /\$\$([^$]+?)\$\$|\$([^$\n]+?)\$/g;

    // A fill-in-the-blank gap written inside a formula ("\frac{_____}{15}")
    // is not LaTeX - KaTeX reads the underscores as subscripts with nothing
    // to attach to and prints the raw source in red. Drawn as an empty answer
    // box instead; data-latex keeps the author's underscores for the editor.
    const texGaps = tex => tex.replace(/(?:\\?_){3,}/g, '{\\boxed{\\phantom{00}}}');

    // esc() plus $...$/$$...$$ LaTeX rendering - used wherever AI-generated
    // question/answer/table content may contain maths notation (fractions,
    // powers, surds). Falls back to plain esc() if katex isn't available.
    function mathText(str) {
        const katex = katexLib();
        if (!katex) return esc(str);
        const s = String(str == null ? '' : str);
        if (!s.includes('$')) return esc(s);
        let out = '';
        let lastIndex = 0;
        let match;
        MATH_REGEX.lastIndex = 0;
        while ((match = MATH_REGEX.exec(s)) !== null) {
            out += esc(s.slice(lastIndex, match.index));
            const isBlock = match[1] !== undefined;
            const tex = isBlock ? match[1] : match[2];
            // Wrapped exactly as public/js/mathRender.js wraps it: the class,
            // contenteditable="false" and data-latex are what let the editor
            // swap a formula back to its raw $source$ when you click into the
            // field and re-render it when you leave. Inert attributes on the
            // print and PDF side, which is why they are emitted unconditionally
            // rather than only in edit mode.
            const cls = isBlock ? 'katex-display-wrap' : 'katex-inline';
            let inner;
            try {
                inner = katex.renderToString(texGaps(tex), { throwOnError: false, displayMode: isBlock });
            } catch {
                inner = esc(tex);
            }
            out += `<span class="${cls}" contenteditable="false" data-latex="${escAttr(tex)}">${inner}</span>`;
            lastIndex = MATH_REGEX.lastIndex;
        }
        out += esc(s.slice(lastIndex));
        return out;
    }

    // ── Marks ─────────────────────────────────────────────

    function partsMarks(q) {
        return (q.parts || []).reduce((sum, p) => sum + Math.max(1, parseInt(p.marks, 10) || 1), 0);
    }

    function questionMarks(type, q) {
        if ((type === 'short_answer' || type === 'extended_response') && q.parts?.length) return partsMarks(q);
        if (type === 'short_answer')      return Math.max(1, parseInt(q.marks, 10) || 1);
        if (type === 'extended_response') return Math.max(1, parseInt(q.marks, 10) || 4);
        if (type === 'matching')          return (q.pairs || []).length;
        return 1;
    }

    function totalMarks(data) {
        let total = 0;
        (data.sections || []).forEach(s => {
            (s.questions || []).forEach(q => { total += questionMarks(s.type, q); });
        });
        return total;
    }

    // ── Deterministic shuffle for matching right-hand column ─
    // Seeded by the pair contents so browser print and server PDF
    // always produce the same ordering for the same worksheet.

    function hashString(str) {
        let h = 2166136261;
        for (let i = 0; i < str.length; i++) {
            h ^= str.charCodeAt(i);
            h = Math.imul(h, 16777619);
        }
        return h >>> 0;
    }

    function seededShuffleIndices(n, seed) {
        const idx = Array.from({ length: n }, (_, i) => i);
        let s = seed || 1;
        const rand = () => {
            // xorshift32
            s ^= s << 13; s >>>= 0;
            s ^= s >> 17;
            s ^= s << 5;  s >>>= 0;
            return s / 4294967296;
        };
        for (let i = n - 1; i > 0; i--) {
            const j = Math.floor(rand() * (i + 1));
            [idx[i], idx[j]] = [idx[j], idx[i]];
        }
        return idx;
    }

    /** Returns { order, letters } where order[k] = original pair index shown
     *  at right-column position k, and letters[i] = letter assigned to the
     *  right-hand item of original pair i. */
    function matchingLayout(q) {
        const pairs = q.pairs || [];
        const seed = hashString(pairs.map(p => (p.left || '') + '|' + (p.right || '')).join('~')) || 1;
        const order = seededShuffleIndices(pairs.length, seed);
        const LETTERS = 'ABCDEFGHIJ';
        const letters = [];
        order.forEach((origIdx, pos) => { letters[origIdx] = LETTERS[pos] || String(pos + 1); });
        return { order, letters };
    }

    // ── Question renderers (student copy) ─────────────────

    function answerLines(count) {
        let out = '<div class="ws-answer-lines">';
        for (let i = 0; i < count; i++) out += '<div class="ws-answer-line"></div>';
        return out + '</div>';
    }

    function marksTag(n) {
        return `<span class="ws-marks-tag">[${n} mark${n === 1 ? '' : 's'}]</span>`;
    }

    /**
     * The marks tag, with - and + either side of it in the editor. `path` is
     * the question or part the marks belong to; the current value rides on
     * the buttons so the editor does not have to know each type's default.
     */
    function marksHtml(path, n) {
        if (!EDIT) return marksTag(n);
        const btn = (act, label, glyph) => `<button type="button" class="ws-ed-btn ws-ed-marks-btn" data-ws-act="${act}"`
            + ` data-ws-target="${escAttr(path)}" data-ws-type="${n}" title="${label}" aria-label="${label}">${glyph}</button>`;
        return `<span class="ws-ed-marks" contenteditable="false">${btn('marks-dec', 'One mark fewer', '&minus;')}${marksTag(n)}${btn('marks-inc', 'One mark more', '+')}</span>`;
    }

    /** True while the editor is showing the teacher's copy. */
    function keyOn() { return !!(EDIT && EDIT.answers); }

    /**
     * The answer space of a written question. On the teacher's copy the model
     * answer is written on the ruled lines themselves, in green, and typed
     * there - the same space it would fill on paper, growing if it is longer.
     */
    function answerSpace(count, path, str, ph) {
        if (!keyOn()) return answerLines(count);
        return `<div class="ws-ed-ruled" style="min-height: calc(${count} * var(--ws-ed-pitch))">`
            + ed(path, str, 'ws-ed-keytext', ph || 'Model answer (teacher only)') + `</div>`;
    }

    /** A short answer that sits on the question's own line: a fill-in word. */
    function keyChip(path, str, ph) {
        if (!keyOn()) return '';
        return ` <span class="ws-ed-keychip" contenteditable="false"><span class="ws-ed-keychip-tick" aria-hidden="true">&#10003;</span>${ed(path, str, 'ws-ed-keytext', ph)}</span>`;
    }

    const PART_LETTERS = 'abcdefghijklmnop';

    /**
     * A real printed table for a part that needs genuine data (as opposed to
     * a diagram/graph, which still can't be rendered and stays a text
     * description folded into the part's own question text).
     *
     * `table.blank` means: row 1 is a worked example, shown in full; every
     * OTHER row keeps its first cell (the feature/label) but blanks every
     * column after it – that's where the student writes their own answer.
     * A single trailing "blank" column can't hold a real two-way comparison
     * (there's nowhere to put two things' worth of contrast in one cell),
     * so this blanks every non-label column, not just the last one.
     */
    function partTableHtml(table, path) {
        if (!table || !table.headers?.length || !table.rows?.length) return '';
        const head = table.headers.map((h, i) => `<th>${ed(`${path}.headers.${i}`, h, '', 'Heading')}</th>`).join('');
        // A blanked cell holds the answer the student is meant to write, so it
        // stays out of the sheet in both modes - editing it here would print
        // the answer on the student copy. It is reachable from the inspector.
        const body = table.rows.map((row, r) =>
            `<tr>${row.map((cell, c) =>
                table.blank && r > 0 && c > 0 ? '<td class="ws-table-blank-cell"></td>' : `<td>${ed(`${path}.rows.${r}.${c}`, cell, '', 'Cell')}</td>`
            ).join('')}</tr>`
        ).join('');
        return `<table class="ws-part-table">${table.blank ? '<caption>Complete the table. Row 1 is done for you.</caption>' : ''}<thead><tr>${head}</tr></thead><tbody>${body}</tbody></table>`;
    }

    /**
     * Simple linear regression (least-squares), used as the "line of best
     * fit" drawn on a plotted graph – a straight-line approximation of what
     * a student draws by eye, not a smoothed curve.
     */
    function linearFit(points) {
        const n = points.length;
        const sumX = points.reduce((a, p) => a + p.x, 0);
        const sumY = points.reduce((a, p) => a + p.y, 0);
        const sumXY = points.reduce((a, p) => a + p.x * p.y, 0);
        const sumXX = points.reduce((a, p) => a + p.x * p.x, 0);
        const denom = n * sumXX - sumX * sumX;
        if (denom === 0) return { m: 0, b: sumY / n };
        const m = (n * sumXY - sumX * sumY) / denom;
        const b = (sumY - m * sumX) / n;
        return { m, b };
    }

    /** Evenly-spaced tick values between min and max, `count` of them. */
    function ticks(min, max, count) {
        const out = [];
        for (let i = 0; i <= count; i++) out.push(min + ((max - min) * i) / count);
        return out;
    }

    function fmtTick(n) {
        const r = Math.round(n * 100) / 100;
        return Number.isInteger(r) ? String(r) : r.toFixed(2).replace(/0+$/, '').replace(/\.$/, '');
    }

    /**
     * Real axes and (optionally) a plotted line/points, as plain SVG – no
     * charting library, consistent with the rest of this file building HTML
     * strings directly. `plotted` false draws blank axes only (a "graph to
     * plot" step on the student copy); true adds the data points and a
     * straight best-fit line (a "graph to read" step, or the answer-key
     * version of a "graph to plot" step).
     */
    function renderGraphSvg(graph, plotted) {
        if (!graph || !Array.isArray(graph.points) || graph.points.length < 2) return '';
        const W = 320, H = 230, padL = 42, padR = 12, padT = 10, padB = 34;
        const plotW = W - padL - padR, plotH = H - padT - padB;
        const xRange = (graph.xMax - graph.xMin) || 1;
        const yRange = (graph.yMax - graph.yMin) || 1;
        const sx = (x) => padL + ((x - graph.xMin) / xRange) * plotW;
        const sy = (y) => padT + plotH - ((y - graph.yMin) / yRange) * plotH;

        const xTicks = ticks(graph.xMin, graph.xMax, 5);
        const yTicks = ticks(graph.yMin, graph.yMax, 5);
        const gridLines = [
            ...xTicks.map(t => `<line x1="${sx(t)}" y1="${padT}" x2="${sx(t)}" y2="${padT + plotH}" class="ws-graph-grid"/>`),
            ...yTicks.map(t => `<line x1="${padL}" y1="${sy(t)}" x2="${padL + plotW}" y2="${sy(t)}" class="ws-graph-grid"/>`),
        ].join('');
        const xLabels = xTicks.map(t => `<text x="${sx(t)}" y="${padT + plotH + 14}" class="ws-graph-tick" text-anchor="middle">${fmtTick(t)}</text>`).join('');
        const yLabels = yTicks.map(t => `<text x="${padL - 6}" y="${sy(t) + 3}" class="ws-graph-tick" text-anchor="end">${fmtTick(t)}</text>`).join('');

        let dataHtml = '';
        if (plotted) {
            const points = graph.points.map(p => `<circle cx="${sx(p.x)}" cy="${sy(p.y)}" r="2.6" class="ws-graph-point"/>`).join('');
            let lineHtml;
            if (graph.toPlot && !graph.curve) {
                // The completed answer-key version of a "graph to plot" step –
                // these are genuinely (roughly) linear relationships the
                // student was asked to draw a line of best fit through, so a
                // straight regression line is the correct answer.
                const { m, b } = linearFit(graph.points);
                const lineY1 = m * graph.xMin + b, lineY2 = m * graph.xMax + b;
                lineHtml = `<line x1="${sx(graph.xMin)}" y1="${sy(lineY1)}" x2="${sx(graph.xMax)}" y2="${sy(lineY2)}" class="ws-graph-fit-line"/>`;
            } else {
                // Either a "graph to read" step (a given, already-complete
                // graph whose shape can be anything - a hormone-concentration
                // spike, an absorption spectrum) or the completed answer-key
                // version of a "graph to plot curve" step (a parabola,
                // reciprocal, etc.) - both are connected point-to-point rather
                // than forced through a straight best-fit line, which would
                // misrepresent non-linear data.
                const sorted = [...graph.points].sort((a, b) => a.x - b.x);
                const path = sorted.map((p, i) => `${i === 0 ? 'M' : 'L'} ${sx(p.x)},${sy(p.y)}`).join(' ');
                lineHtml = `<path d="${path}" class="ws-graph-fit-line" fill="none"/>`;
            }
            dataHtml = `${lineHtml}${points}`;
        }

        return `<svg class="ws-part-svg" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">
            ${gridLines}
            <line x1="${padL}" y1="${padT}" x2="${padL}" y2="${padT + plotH}" class="ws-graph-axis"/>
            <line x1="${padL}" y1="${padT + plotH}" x2="${padL + plotW}" y2="${padT + plotH}" class="ws-graph-axis"/>
            ${xLabels}${yLabels}
            <text x="${padL + plotW / 2}" y="${H - 2}" class="ws-graph-axis-title" text-anchor="middle">${esc(graph.xLabel || '')}</text>
            <text x="10" y="${padT + plotH / 2}" class="ws-graph-axis-title" text-anchor="middle" transform="rotate(-90 10 ${padT + plotH / 2})">${esc(graph.yLabel || '')}</text>
            ${dataHtml}
        </svg>`;
    }

    /**
     * A fully-drawn reaction-profile energy curve (start -> peak -> end),
     * given to the student, not left blank – freehand curve drawing isn't
     * something this renderer can meaningfully leave for the student to do.
     */
    function renderReactionProfileSvg(rp) {
        if (!rp || !Number.isFinite(rp.startEnergy) || !Number.isFinite(rp.peakEnergy) || !Number.isFinite(rp.endEnergy)) return '';
        const W = 320, H = 200, padL = 20, padR = 20, padT = 20, padB = 30;
        const plotW = W - padL - padR, plotH = H - padT - padB;
        const lo = Math.min(rp.startEnergy, rp.endEnergy);
        const hi = rp.peakEnergy;
        const range = (hi - lo) || 1;
        const sy = (e) => padT + plotH - ((e - lo) / range) * plotH;
        const x0 = padL + plotW * 0.08, xPeak = padL + plotW * 0.5, x1 = padL + plotW * 0.92;
        const y0 = sy(rp.startEnergy), yPeak = sy(rp.peakEnergy), y1 = sy(rp.endEnergy);
        const path = `M ${x0},${y0} Q ${(x0 + xPeak) / 2},${y0} ${xPeak},${yPeak} Q ${(xPeak + x1) / 2},${y1} ${x1},${y1}`;
        return `<svg class="ws-part-svg" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">
            <line x1="${padL}" y1="${padT}" x2="${padL}" y2="${padT + plotH}" class="ws-graph-axis"/>
            <line x1="${padL}" y1="${padT + plotH}" x2="${padL + plotW}" y2="${padT + plotH}" class="ws-graph-axis"/>
            <path d="${path}" class="ws-graph-fit-line" fill="none"/>
            <text x="${x0}" y="${y0 - 6}" class="ws-graph-tick" text-anchor="start">${esc(rp.substancesLabel || '')}</text>
            <text x="${x1}" y="${y1 - 6}" class="ws-graph-tick" text-anchor="end">${esc(rp.productsLabel || '')}</text>
            <text x="${padL + plotW / 2}" y="${H - 2}" class="ws-graph-axis-title" text-anchor="middle">Progress of reaction</text>
            <text x="8" y="${padT + plotH / 2}" class="ws-graph-axis-title" text-anchor="middle" transform="rotate(-90 8 ${padT + plotH / 2})">Energy</text>
        </svg>`;
    }

    /**
     * A fully-drawn electron-shell diagram (concentric shells, dots for
     * electrons) for one named atom/ion – given, not drawn from scratch,
     * same reasoning as the reaction profile above.
     */
    function renderElectronShellsSvg(es) {
        if (!es || !es.symbol || !Array.isArray(es.shells) || !es.shells.length) return '';
        const size = 220, cx = size / 2, cy = size / 2;
        const maxR = Math.min(size, size) / 2 - 14;
        const step = maxR / es.shells.length;
        const circles = es.shells.map((_, i) => {
            const r = step * (i + 1);
            return `<circle cx="${cx}" cy="${cy}" r="${r}" class="ws-shell-circle"/>`;
        }).join('');
        const dots = es.shells.map((count, i) => {
            const r = step * (i + 1);
            const pts = [];
            for (let k = 0; k < count; k++) {
                const angle = (2 * Math.PI * k) / count - Math.PI / 2;
                pts.push(`<circle cx="${cx + r * Math.cos(angle)}" cy="${cy + r * Math.sin(angle)}" r="2.8" class="ws-shell-electron"/>`);
            }
            return pts.join('');
        }).join('');
        return `<svg class="ws-part-svg" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
            ${circles}${dots}
            <text x="${cx}" y="${cy + 5}" class="ws-shell-label" text-anchor="middle">${esc(es.symbol)}${es.charge ? `<tspan class="ws-shell-charge">${esc(es.charge)}</tspan>` : ''}</text>
        </svg>`;
    }

    /**
     * A fully-drawn geometric diagram – one or two labeled polygons/line
     * segments, optionally on a coordinate grid, with angle and/or length
     * labels. Given to the student, not constructed from scratch: covers
     * angle-chasing (one shape, some angles given, one marked "x"),
     * similar/congruent shape comparisons (two shapes with labeled sides),
     * and translations/vectors (two shapes on a grid). Circle theorems and
     * bearings use a different visual primitive and aren't covered here.
     */
    function renderShapeDiagramSvg(sd) {
        if (!sd || !Array.isArray(sd.shapes) || !sd.shapes.length) return '';
        const allVerts = sd.shapes.flatMap(s => s.vertices || []);
        if (!allVerts.length) return '';

        const W = 320, H = 260, padL = 30, padR = 30, padT = 24, padB = 30;
        const plotW = W - padL - padR, plotH = H - padT - padB;

        let xMin, xMax, yMin, yMax;
        if (sd.gridded && [sd.xMin, sd.xMax, sd.yMin, sd.yMax].every(Number.isFinite)) {
            ({ xMin, xMax, yMin, yMax } = sd);
        } else {
            const xs = allVerts.map(v => v.x), ys = allVerts.map(v => v.y);
            const rawXMin = Math.min(...xs), rawXMax = Math.max(...xs);
            const rawYMin = Math.min(...ys), rawYMax = Math.max(...ys);
            const marginX = (rawXMax - rawXMin) * 0.25 || 1;
            const marginY = (rawYMax - rawYMin) * 0.25 || 1;
            xMin = rawXMin - marginX; xMax = rawXMax + marginX;
            yMin = rawYMin - marginY; yMax = rawYMax + marginY;
        }
        const xRange = (xMax - xMin) || 1, yRange = (yMax - yMin) || 1;
        const sx = (x) => padL + ((x - xMin) / xRange) * plotW;
        const sy = (y) => padT + plotH - ((y - yMin) / yRange) * plotH;

        let gridHtml = '';
        if (sd.gridded) {
            // Unit-square grid (step 1), not ticks(min,max,5) like a data graph -
            // shape vertices are placed at integer coordinates, so the grid has
            // to fall on every integer too or shapes float between the lines
            // instead of sitting on intersections, exactly like real squared
            // paper for a translation/reflection question.
            const xTicks = []; for (let t = Math.ceil(xMin); t <= xMax; t++) xTicks.push(t);
            const yTicks = []; for (let t = Math.ceil(yMin); t <= yMax; t++) yTicks.push(t);
            const gridLines = [
                ...xTicks.map(t => `<line x1="${sx(t)}" y1="${padT}" x2="${sx(t)}" y2="${padT + plotH}" class="ws-graph-grid"/>`),
                ...yTicks.map(t => `<line x1="${padL}" y1="${sy(t)}" x2="${padL + plotW}" y2="${sy(t)}" class="ws-graph-grid"/>`),
            ].join('');
            gridHtml = `${gridLines}
            <line x1="${padL}" y1="${padT}" x2="${padL}" y2="${padT + plotH}" class="ws-graph-axis"/>
            <line x1="${padL}" y1="${padT + plotH}" x2="${padL + plotW}" y2="${padT + plotH}" class="ws-graph-axis"/>`;
        }

        const shapesHtml = sd.shapes.map(shape => {
            const verts = shape.vertices || [];
            if (verts.length < 2) return '';
            const closed = shape.closed !== false && verts.length > 2;
            const cx = verts.reduce((a, v) => a + sx(v.x), 0) / verts.length;
            const cy = verts.reduce((a, v) => a + sy(v.y), 0) / verts.length;

            const d = verts.map((v, i) => `${i === 0 ? 'M' : 'L'} ${sx(v.x)},${sy(v.y)}`).join(' ') + (closed ? ' Z' : '');
            const pathCls = shape.style === 'dashed' ? 'ws-shape-outline ws-shape-dashed' : 'ws-shape-outline';
            const outline = `<path d="${d}" class="${pathCls}" fill="none"/>`;

            // Vertex + angle labels: offset away from the shape's centroid so
            // text doesn't sit on top of the lines it's labelling.
            const vertexLabels = verts.map(v => {
                const px = sx(v.x), py = sy(v.y);
                const dx = px - cx, dy = py - cy;
                const len = Math.hypot(dx, dy) || 1;
                const lx = px + (dx / len) * 14, ly = py + (dy / len) * 14;
                let html = '';
                if (v.vertexLabel) html += `<text x="${lx}" y="${ly}" class="ws-shape-vertex-label" text-anchor="middle">${mathText(v.vertexLabel)}</text>`;
                if (v.angleLabel) {
                    // Interior angles (the usual case, e.g. angle-chasing inside a
                    // triangle) sit inward, toward the centroid, opposite sign
                    // from the vertex label above. Exterior angles (a polygon's
                    // exterior angle between the shape and an extended dashed
                    // ray) sit further outward instead, past the vertex label -
                    // the offset distances differ so the two never overlap.
                    const angleDist = v.angleSide === 'exterior' ? 26 : -14;
                    html += `<text x="${px + (dx / len) * angleDist}" y="${py + (dy / len) * angleDist}" class="ws-shape-angle-label" text-anchor="middle">${mathText(v.angleLabel)}</text>`;
                }
                // A bearings diagram's north line - always points straight up
                // the page (screen-space, not data-space), same convention as
                // a real bearings question's north arrow at the point of
                // interest, with the bearing angle then read clockwise from it.
                if (v.northArrow) {
                    html += `<line x1="${px}" y1="${py}" x2="${px}" y2="${py - 22}" class="ws-shape-north-line" marker-end="url(#ws-shape-north-arrowhead)"/>`;
                    html += `<text x="${px}" y="${py - 26}" class="ws-shape-north-label" text-anchor="middle">N</text>`;
                }
                return html;
            }).join('');

            // Edge labels: midpoint of the referenced edge, offset away from centroid.
            const edgeCount = closed ? verts.length : verts.length - 1;
            const edgeLabels = (shape.edgeLabels || []).map(el => {
                if (!Number.isInteger(el.edgeIndex) || el.edgeIndex < 0 || el.edgeIndex >= edgeCount || !el.text) return '';
                const a = verts[el.edgeIndex], b = verts[(el.edgeIndex + 1) % verts.length];
                const mx = (sx(a.x) + sx(b.x)) / 2, my = (sy(a.y) + sy(b.y)) / 2;
                const dx = mx - cx, dy = my - cy;
                const len = Math.hypot(dx, dy) || 1;
                const lx = mx + (dx / len) * 12, ly = my + (dy / len) * 12;
                return `<text x="${lx}" y="${ly}" class="ws-shape-edge-label" text-anchor="middle">${mathText(el.text)}</text>`;
            }).join('');

            const shapeTitle = shape.label
                ? `<text x="${cx}" y="${Math.min(...verts.map(v => sy(v.y))) - 10}" class="ws-shape-title" text-anchor="middle">${mathText(shape.label)}</text>`
                : '';

            return outline + vertexLabels + edgeLabels + shapeTitle;
        }).join('');

        return `<svg class="ws-part-svg" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">
            <defs><marker id="ws-shape-north-arrowhead" markerWidth="7" markerHeight="7" refX="3" refY="5" orient="auto"><path d="M0,5 L3,0 L6,5 Z" class="ws-shape-north-arrowhead-fill"/></marker></defs>
            ${gridHtml}
            ${shapesHtml}
        </svg>`;
    }

    /**
     * A number line with an optional shaded/highlighted region and open or
     * closed endpoint circles – for representing inequalities. Given to the
     * student as a fully-drawn diagram to read or to interpret, not left
     * blank (marking a region on a number line is a drawing task like a
     * sketch-graph, which this renderer can't leave blank for the student).
     */
    function renderNumberLineSvg(nl) {
        if (!nl || !Number.isFinite(nl.min) || !Number.isFinite(nl.max)) return '';
        const W = 320, H = 70, padL = 20, padR = 20;
        const lineY = 34;
        const range = (nl.max - nl.min) || 1;
        const sx = (v) => padL + ((v - nl.min) / range) * (W - padL - padR);

        const step = Number.isFinite(nl.tickStep) && nl.tickStep > 0 ? nl.tickStep : 1;
        const tickVals = [];
        for (let v = Math.ceil(nl.min / step) * step; v <= nl.max + 1e-9; v += step) tickVals.push(Math.round(v * 1000) / 1000);
        const tickHtml = tickVals.map(v => `
            <line x1="${sx(v)}" y1="${lineY - 5}" x2="${sx(v)}" y2="${lineY + 5}" class="ws-numline-tick"/>
            <text x="${sx(v)}" y="${lineY + 22}" class="ws-numline-label" text-anchor="middle">${fmtTick(v)}</text>`).join('');

        const points = Array.isArray(nl.points) ? nl.points : [];
        const regionHtml = points.length === 2
            ? `<line x1="${sx(points[0].value)}" y1="${lineY}" x2="${sx(points[1].value)}" y2="${lineY}" class="ws-numline-region"/>`
            : '';
        const pointHtml = points.map(p => {
            const cls = p.filled ? 'ws-numline-point-filled' : 'ws-numline-point-open';
            return `<circle cx="${sx(p.value)}" cy="${lineY}" r="5" class="${cls}"/>`;
        }).join('');
        const arrowHtml = (points.length === 1 && (points[0].direction === 'left' || points[0].direction === 'right'))
            ? (() => {
                const x0 = sx(points[0].value);
                const x1 = points[0].direction === 'right' ? W - padR + 6 : padL - 6;
                return `<line x1="${x0}" y1="${lineY}" x2="${x1}" y2="${lineY}" class="ws-numline-region" marker-end="url(#ws-numline-arrowhead)"/>`;
            })()
            : '';

        return `<svg class="ws-part-svg" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">
            <defs><marker id="ws-numline-arrowhead" markerWidth="8" markerHeight="8" refX="6" refY="3" orient="auto"><path d="M0,0 L6,3 L0,6 Z" class="ws-numline-arrowhead-fill"/></marker></defs>
            <line x1="${padL}" y1="${lineY}" x2="${W - padR}" y2="${lineY}" class="ws-numline-axis"/>
            ${tickHtml}
            ${regionHtml}
            ${arrowHtml}
            ${pointHtml}
        </svg>`;
    }

    /**
     * A fully-drawn circle-theorem diagram: a circle with labelled points on
     * its circumference, chords/radii between them, an optional tangent line
     * at one point, and angle labels - covers angle-at-centre-vs-
     * circumference, cyclic quadrilaterals, and tangent/alternate-segment
     * questions. Given to the student, not constructed from scratch.
     */
    function renderCircleDiagramSvg(cd) {
        if (!cd || !Array.isArray(cd.points) || cd.points.length < 2) return '';
        const size = 260, cx = size / 2, cy = size / 2 + 6, r = 92;
        const toXY = (angleDeg) => {
            const rad = (angleDeg * Math.PI) / 180;
            return { x: cx + r * Math.sin(rad), y: cy - r * Math.cos(rad) };
        };
        const pts = cd.points.map(p => toXY(p.angleDeg));

        const centerHtml = cd.centerLabel
            ? `<circle cx="${cx}" cy="${cy}" r="2" class="ws-circle-center-dot"/><text x="${cx + 8}" y="${cy - 6}" class="ws-shape-vertex-label" text-anchor="middle">${mathText(cd.centerLabel)}</text>`
            : '';

        const chordsHtml = (cd.chords || []).map(ch => {
            if (!Number.isInteger(ch.fromIndex) || ch.fromIndex < 0 || ch.fromIndex >= pts.length) return '';
            const from = pts[ch.fromIndex];
            const to = ch.toIndex === -1 ? { x: cx, y: cy } : pts[ch.toIndex];
            if (!to || (ch.toIndex !== -1 && (!Number.isInteger(ch.toIndex) || ch.toIndex < 0 || ch.toIndex >= pts.length))) return '';
            const cls = ch.style === 'dashed' ? 'ws-shape-outline ws-shape-dashed' : 'ws-shape-outline';
            return `<line x1="${from.x}" y1="${from.y}" x2="${to.x}" y2="${to.y}" class="${cls}"/>`;
        }).join('');

        // Tangent's two perpendicular-to-radius direction candidates - stored
        // (rather than only drawn) so the angle-label bisector below can use
        // whichever one is relevant, for alternate-segment-theorem diagrams
        // where the marked angle sits between the tangent and a chord.
        let tangentDir = null;
        let tangentHtml = '';
        if (Number.isInteger(cd.tangentAtIndex) && cd.tangentAtIndex >= 0 && cd.tangentAtIndex < pts.length) {
            const p = cd.points[cd.tangentAtIndex];
            const rad = (p.angleDeg * Math.PI) / 180;
            // Tangent is perpendicular to the radius at this point - same
            // direction convention as toXY's radius vector (sin, -cos), so a
            // perpendicular to that is (cos, sin) for BOTH x and y (not a
            // mixed sign - that was the bug that made the "tangent" cut
            // across the circle instead of grazing it at one point).
            const tx = Math.cos(rad), ty = Math.sin(rad);
            tangentDir = { x: tx, y: ty };
            const pt = pts[cd.tangentAtIndex];
            const len = 55;
            tangentHtml = `<line x1="${pt.x - tx * len}" y1="${pt.y - ty * len}" x2="${pt.x + tx * len}" y2="${pt.y + ty * len}" class="ws-shape-outline"/>`;
        }

        // Direction(s) of every chord/radius/tangent touching point i, used
        // to find the true bisector of the marked angle - "toward/away from
        // the circle's centre" only happens to be right when the two edges
        // are symmetric about that direction, which usually isn't the case.
        function edgeDirectionsAt(i) {
            const dirs = [];
            (cd.chords || []).forEach(ch => {
                let other = null;
                if (ch.fromIndex === i) other = ch.toIndex === -1 ? { x: cx, y: cy } : pts[ch.toIndex];
                else if (ch.toIndex === i) other = pts[ch.fromIndex];
                if (other) {
                    const ex = other.x - pts[i].x, ey = other.y - pts[i].y;
                    const elen = Math.hypot(ex, ey) || 1;
                    dirs.push({ x: ex / elen, y: ey / elen });
                }
            });
            if (cd.tangentAtIndex === i && tangentDir) dirs.push(tangentDir);
            return dirs;
        }

        const pointsHtml = cd.points.map((p, i) => {
            const pt = pts[i];
            const dx = pt.x - cx, dy = pt.y - cy;
            const len = Math.hypot(dx, dy) || 1;
            const lx = pt.x + (dx / len) * 14, ly = pt.y + (dy / len) * 14;
            let html = `<circle cx="${pt.x}" cy="${pt.y}" r="2" class="ws-graph-point"/>`;
            if (p.label) html += `<text x="${lx}" y="${ly}" class="ws-shape-vertex-label" text-anchor="middle">${mathText(p.label)}</text>`;
            if (p.angleLabel) {
                const dirs = edgeDirectionsAt(i);
                let bx = dx / len, by = dy / len; // fallback: toward/away from centre
                if (dirs.length >= 2) {
                    const sx = dirs[0].x + dirs[1].x, sy = dirs[0].y + dirs[1].y;
                    const slen = Math.hypot(sx, sy);
                    if (slen > 0.05) { bx = sx / slen; by = sy / slen; }
                }
                const angleDist = p.angleSide === 'exterior' ? 20 : 18;
                const sign = p.angleSide === 'exterior' ? -1 : 1;
                html += `<text x="${pt.x + bx * angleDist * sign}" y="${pt.y + by * angleDist * sign}" class="ws-shape-angle-label" text-anchor="middle">${mathText(p.angleLabel)}</text>`;
            }
            return html;
        }).join('');

        return `<svg class="ws-part-svg" viewBox="0 0 ${size} ${size}" xmlns="http://www.w3.org/2000/svg">
            <circle cx="${cx}" cy="${cy}" r="${r}" class="ws-shape-outline" fill="none"/>
            ${centerHtml}
            ${chordsHtml}
            ${tangentHtml}
            ${pointsHtml}
        </svg>`;
    }

    /**
     * A two-stage probability tree - first-stage branches, each with its own
     * (possibly conditional, e.g. "without replacement") second-stage
     * branches. Given to the student as a fully-drawn diagram to read
     * probabilities from, not left blank - filling in a tree's structure
     * from scratch is a drawing task this renderer can't leave open-ended.
     */
    function renderProbabilityTreeSvg(pt) {
        if (!pt || !Array.isArray(pt.branches) || !pt.branches.length) return '';
        const W = 340, H = 230;
        const rootX = 20, midX = 150, endX = 300;
        const n1 = pt.branches.length;
        const stage1Y = pt.branches.map((_, i) => 20 + (i + 0.5) * ((H - 20) / n1));
        const rootY = H / 2;

        let html = '';
        pt.branches.forEach((b1, i) => {
            const y1 = stage1Y[i];
            html += `<line x1="${rootX}" y1="${rootY}" x2="${midX}" y2="${y1}" class="ws-shape-outline"/>`;
            const midLabelX = (rootX + midX) / 2, midLabelY = (rootY + y1) / 2;
            html += `<text x="${midLabelX}" y="${midLabelY - 5}" class="ws-shape-edge-label" text-anchor="middle">${mathText(b1.probability || '')}</text>`;
            html += `<text x="${midX + 6}" y="${y1 + 3}" class="ws-shape-vertex-label" text-anchor="start">${mathText(b1.label || '')}</text>`;

            const children = Array.isArray(b1.children) ? b1.children : [];
            const n2 = children.length || 1;
            const bandTop = y1 - (H / n1) * 0.42, bandH = (H / n1) * 0.84;
            children.forEach((b2, j) => {
                const y2 = bandTop + (j + 0.5) * (bandH / n2);
                html += `<line x1="${midX}" y1="${y1}" x2="${endX}" y2="${y2}" class="ws-shape-outline"/>`;
                const eLabelX = (midX + endX) / 2, eLabelY = (y1 + y2) / 2;
                html += `<text x="${eLabelX}" y="${eLabelY - 5}" class="ws-shape-edge-label" text-anchor="middle">${mathText(b2.probability || '')}</text>`;
                html += `<text x="${endX + 6}" y="${y2 + 3}" class="ws-shape-vertex-label" text-anchor="start">${mathText(b2.label || '')}</text>`;
            });
        });

        return `<svg class="ws-part-svg" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">${html}</svg>`;
    }

    /**
     * A curve diagram - axes, curves, their crossings and any shaded areas.
     * `complete` false prints the axes alone, for a step that asks the student
     * to draw the diagram; the finished one then belongs on the answer key.
     */
    function renderCurveDiagramSvg(cd, complete) {
        if (!cd || !CurveDiagram) return '';
        return CurveDiagram.render(cd, { className: 'ws-part-svg ws-curve-diagram', blank: !complete });
    }

    /**
     * A question's own diagram, printed between its text and wherever the
     * student answers: complete when it is given, blank axes when it is theirs
     * to draw. Parts carry theirs separately - see partsHtml.
     */
    function questionDiagramHtml(q) {
        return q && q.curveDiagram ? renderCurveDiagramSvg(q.curveDiagram, !q.curveDiagram.toDraw) : '';
    }

    /**
     * An answer-key entry with the finished diagram added, for a question the
     * student was asked to draw one for. Goes inside the entry's own box, and
     * makes one when the question has no written answer to hang it on.
     */
    function withKeyDiagram(block, q, n) {
        const drawn = q && q.curveDiagram && q.curveDiagram.toDraw ? renderCurveDiagramSvg(q.curveDiagram, true) : '';
        if (!drawn) return block;
        const at = block.lastIndexOf('</div>');
        if (at === -1) return `<div class="ak-indicative"><p class="ak-ind-q">${n}. ${mathText(q.question)}</p>${drawn}</div>`;
        return block.slice(0, at) + drawn + block.slice(at);
    }

    /** An original AI-composed source passage, formatted like a real exam source box. */
    function renderSourceHtml(source, path) {
        if (!source || !source.label || !source.text) return '';
        return `<div class="ws-source-box"><p class="ws-source-label">${ed(`${path}.label`, source.label, '', 'Source label')}</p><p class="ws-source-text">${ed(`${path}.text`, source.text, '', 'Source text')}</p></div>`;
    }

    /**
     * A preformatted, monospace, whitespace-preserving code/pseudocode
     * listing - plain esc() rather than mathText(), since code routinely
     * contains its own $ / \ characters that must never be run through
     * LaTeX rendering.
     */
    function renderCodeBlockHtml(code, path) {
        if (!code || !code.text) return '';
        const label = code.language ? `<p class="ws-code-label">${esc(code.language)}</p>` : '';
        // edPlain, not ed: a listing routinely contains its own $ and \, and
        // running it through LaTeX rendering is how a shell variable becomes
        // an equation. Same reason this uses esc() on the print side.
        return `<div class="ws-code-box">${label}<pre class="ws-code-block"><code>${edPlain(`${path}.text`, code.text, 'ws-ed-code', 'Code')}</code></pre></div>`;
    }

    /**
     * Tick-one-box options for a part, in the same visual style as the
     * worksheet's own multiple_choice question type – not a wall of prose
     * inside the question text.
     */
    function partOptionsHtml(options, path) {
        if (!Array.isArray(options) || !options.length) return '';
        const letters = 'ABCDEFGHIJ';
        return `<div class="ws-options ws-part-options">${options.map((o, i) => `<span>${letters[i] || i + 1}) ${ed(`${path}.${i}`, o, '', 'Option')}</span>`).join('')}</div>`;
    }

    /**
     * Lettered sub-parts, exam-style: the question's own text is the shared
     * stem (rendered separately by the caller), this renders each part below
     * it with its own marks tag and answer space sized to that part's marks.
     * A part with a blank table or a set of tick-one-box options has nowhere
     * else for the answer to go, so those get no separate answer lines.
     */
    function partsHtml(q, qPath) {
        // Lettered by position in the rendered list, pathed by index in the
        // stored array. Print drops a part with no text; edit mode keeps it,
        // because that is exactly what a part you just added looks like.
        const entries = (q.parts || [])
            .map((p, i) => ({ p, i }))
            .filter(({ p }) => EDIT ? !!p : (p && p.text));
        if (!entries.length) return '';
        return entries.map(({ p, i }, pos) => {
            const pPath = `${qPath}.parts.${i}`;
            const marks = Math.max(1, parseInt(p.marks, 10) || 1);
            const lines = Math.min(18, Math.max(3, Math.round(marks * 1.5)));
            const hasOptions = Array.isArray(p.options) && p.options.length;
            // Blank axes are the answer space for a "graph to plot" step,
            // same reasoning as a blank fill-in-table cell - no separate lines.
            // (Not so for a diagram to draw: "with the help of a diagram,
            // explain..." is a diagram and a written answer.)
            const isBlankGraph = p.graph?.toPlot;
            return `
            <div class="ws-part">
                <p class="ws-q-text ws-part-text"><span class="ws-part-letter">(${PART_LETTERS[pos] || pos + 1})</span>${ed(`${pPath}.text`, p.text, '', 'Part')} ${marksHtml(pPath, marks)}${EDIT ? edBtn('part-del', pPath, 'Delete this part', ICONS.bin) : ''}</p>
                ${renderSourceHtml(p.source, `${pPath}.source`)}
                ${renderCodeBlockHtml(p.code, `${pPath}.code`)}
                ${partOptionsHtml(p.options, `${pPath}.options`)}
                ${partTableHtml(p.table, `${pPath}.table`)}
                ${partDiagramsHtml(p).join('')}
                ${EDIT && (p.graph || p.reactionProfile || p.electronShells) ? `<div class="ws-ed-inline-add" contenteditable="false">${edBtn('part-data', pPath, 'Edit the numbers behind this diagram', 'Edit diagram data')}</div>` : ''}
                ${p.table?.blank || hasOptions || isBlankGraph
                    ? (keyOn() ? `<p class="ws-ed-keyline">${keyChip(`${pPath}.answer`, p.answer, 'Answer (teacher only)')}</p>` : '')
                    : answerSpace(lines, `${pPath}.answer`, p.answer)}
            </div>`;
        }).join('')
        + (EDIT ? `<div class="ws-ed-inline-add" contenteditable="false">${edBtn('part-add', qPath, 'Add part', '+ Add part')}</div>` : '');
    }

    /**
     * A part's diagrams as printed on the student copy, one string each, in
     * print order. Separate from partsHtml so the Word export (utils/
     * worksheetDocx.js) draws exactly the same pictures.
     */
    function partDiagramsHtml(p) {
        return [
            p.graph ? renderGraphSvg(p.graph, !p.graph.toPlot) : '',
            renderReactionProfileSvg(p.reactionProfile),
            renderElectronShellsSvg(p.electronShells),
            renderShapeDiagramSvg(p.shapeDiagram),
            renderNumberLineSvg(p.numberLine),
            renderCircleDiagramSvg(p.circleDiagram),
            renderProbabilityTreeSvg(p.probabilityTree),
            renderCurveDiagramSvg(p.curveDiagram, !(p.curveDiagram && p.curveDiagram.toDraw)),
        ].filter(Boolean);
    }

    /**
     * The completed versions the answer key adds for what the student had to
     * plot or draw. Empty when there is nothing to complete.
     */
    function partKeyDiagramsHtml(p) {
        return [
            p.graph && p.graph.toPlot ? renderGraphSvg(p.graph, true) : '',
            p.curveDiagram && p.curveDiagram.toDraw ? renderCurveDiagramSvg(p.curveDiagram, true) : '',
        ].filter(Boolean);
    }

    /** Answer-key entry for a parted question: the stem, then each part's indicative answer under its own letter. */
    function partsAnswerHtml(qNum, q) {
        const parts = (q.parts || []).filter(p => p && p.text);
        const rows = parts.map((p, i) => {
            if (!p.answer) return '';
            // A "graph to plot" step shows blank axes on the student copy -
            // the answer key is the one place the completed graph belongs,
            // same reasoning as a real mark scheme showing the finished plot.
            const completedGraph = partKeyDiagramsHtml(p).join('');
            return `<p class="ak-ind-q">(${PART_LETTERS[i] || i + 1}) ${mathText(p.text)} <span class="ak-marks">[${Math.max(1, parseInt(p.marks, 10) || 1)} marks]</span></p><p class="ak-ind-a">${mathText(p.answer)}</p>${completedGraph}`;
        }).join('');
        if (!rows.trim()) return '';
        return `<div class="ak-indicative"><p class="ak-ind-q">${qNum}. ${mathText(q.question)}</p>${rows}</div>`;
    }

    /**
     * Chunked instructions, from the `chunked` support.
     *
     * A real <ol> rather than steps run together in the question text: the
     * point of chunking is one action per line, and a paragraph of numbered
     * sentences is the thing it exists to avoid. Empty when the question has
     * no steps, which is most questions even on a chunked worksheet.
     */
    function stepsHtml(q, qPath) {
        const steps = (q && q.steps) || [];
        if (!Array.isArray(steps)) return '';
        // Print drops blank steps; edit mode keeps them, because a step you
        // have just added is blank by definition and has to be clickable.
        // Either way the path carries the step's index in the stored array,
        // not its position among the ones that survived the filter.
        const items = steps
            .map((s, i) => ({ s, i }))
            .filter(({ s }) => EDIT ? typeof s === 'string' : (typeof s === 'string' && s.trim()))
            .map(({ s, i }) => `<li>${EDIT
                ? edPlain(`${qPath}.steps.${i}`, s, '', 'Step') + edBtn('step-del', `${qPath}.steps.${i}`, 'Remove step', '&times;')
                : esc(s.trim())}</li>`)
            .join('');
        if (!items) return '';
        const add = EDIT ? `<li class="ws-ed-inline-add" contenteditable="false">${edBtn('step-add', qPath, 'Add step', '+ Add step')}</li>` : '';
        return `<ol class="ws-steps">${items}${add}</ol>`;
    }

    /**
     * A worked example at the head of a section, on a supported sheet: a
     * question like the section's, its working and its answer, so the method
     * is on the page before the pupil needs it.
     */
    function workedExampleHtml(section, sPath) {
        const we = section && section.workedExample;
        if (!we || typeof we !== 'object') return '';
        const p = `${sPath}.workedExample`;
        const steps = (Array.isArray(we.steps) ? we.steps : [])
            .map((st, i) => ({ st, i }))
            .filter(({ st }) => typeof st === 'string' && (EDIT || st.trim()))
            .map(({ st, i }) => `<li>${ed(`${p}.steps.${i}`, st, '', 'Step')}</li>`)
            .join('');
        const remove = EDIT ? edBtn('we-del', sPath, 'Remove worked example', '&times;') : '';
        return `<div class="ws-worked">
                <p class="ws-worked-label">Worked example${remove}</p>
                <p class="ws-worked-q">${ed(`${p}.question`, we.question, '', 'Example question')}</p>
                ${steps ? `<ol class="ws-steps">${steps}</ol>` : ''}
                <p class="ws-worked-a"><span class="ws-worked-a-label">Answer:</span> ${ed(`${p}.answer`, we.answer, '', 'Answer')}</p>
            </div>`;
    }

    /**
     * A sentence starter, from the `sentence-starters` support.
     *
     * Printed on the first answer line rather than above it, because that is
     * where the student writes – a starter floating above blank ruling is a
     * hint, and the point is that they carry straight on from it.
     */
    function starterHtml(q, qPath) {
        const raw = q && typeof q.starter === 'string' ? q.starter : '';
        // In edit mode the line stays as long as the field exists at all, even
        // emptied, so clearing a starter does not put it out of reach. A
        // question that never had one gets nothing - adding a starter is an
        // inspector action, not something every question grows a slot for.
        if (EDIT) return q && typeof q.starter === 'string'
            ? `<p class="ws-starter">${edPlain(`${qPath}.starter`, raw, '', 'Sentence starter')}</p>`
            : '';
        return raw.trim() ? `<p class="ws-starter">${esc(raw.trim())}</p>` : '';
    }

    /**
     * An option's letter. On the teacher's copy it is the control that marks
     * the option correct, and the correct one is ringed in green.
     */
    function mcLetter(qPath, q, k) {
        const label = `${k.toUpperCase()})`;
        if (!keyOn()) return label;
        const on = q.answer === k;
        return `<button type="button" class="ws-ed-btn ws-ed-mcl${on ? ' is-correct' : ''}" data-ws-act="mc-answer"`
            + ` data-ws-target="${escAttr(qPath)}" data-ws-type="${k}" aria-pressed="${on}"`
            + ` title="${on ? 'The correct answer' : 'Make this the correct answer'}">${label}</button>`;
    }

    /** True or False, the same: the right one ringed, either one a click to choose. */
    function tfLabel(qPath, q, value) {
        const text = value ? 'True' : 'False';
        if (!keyOn()) return `<span class="ws-tf-label">${text}</span>`;
        const on = q.answer === value;
        return `<button type="button" class="ws-ed-btn ws-tf-label ws-ed-tfl${on ? ' is-correct' : ''}" data-ws-act="tf-answer"`
            + ` data-ws-target="${escAttr(qPath)}" data-ws-type="${value}" aria-pressed="${on}"`
            + ` title="${on ? 'The correct answer' : `Make ${text} the correct answer`}">${text}</button>`;
    }

    /**
     * @param {string} qPath dotted JSON path to this question, for edit mode.
     *        Undefined on the print and PDF path, where nothing is editable.
     *
     * The editable wrapper always goes round the value alone, never round the
     * printed furniture - the question number, the "A)" prefixes, the
     * True/False labels and the marks tag all stay outside it, so a teacher
     * cannot delete the scaffolding of the sheet by selecting a line and
     * typing over it.
     */
    function renderQuestion(type, q, n, qPath, sectionOpts) {
        const so = sectionOpts || {};
        // Steps sit between the question and whatever the student does next,
        // which is where the instruction they describe belongs - unless the
        // whole section shares them, when they are printed once at its head.
        const steps = so.stepsLifted ? '' : stepsHtml(q, qPath);
        const tools = edQuestionTools(qPath, q, type);
        const open  = EDIT
            ? `<div class="ws-question ws-ed-q" data-ws-q="${escAttr(qPath)}">`
            : `<div class="ws-question">`;

        // Three options on a supported sheet, which has no `d` at all; an
        // emptied D on an ordinary sheet still shows, so it can be typed into.
        if (type === 'multiple_choice') return `
            ${open}${tools}
                ${stemHtml(n, `${qPath}.question`, q.question)}
                ${steps}
                <div class="ws-options">
                    ${['a', 'b', 'c', 'd'].filter(k => k !== 'd' || q.d !== undefined)
                        .map(k => `<span>${mcLetter(qPath, q, k)} ${ed(`${qPath}.${k}`, q[k], '', `Option ${k.toUpperCase()}`)}</span>`).join('')}
                </div>
            </div>`;

        if (type === 'true_false') return `
            ${open}${tools}
                <p class="ws-q-text"><span class="ws-q-n">${n}</span>${tfLabel(qPath, q, true)}${tfLabel(qPath, q, false)} ${ed(`${qPath}.statement`, q.statement, '', 'Statement')}</p>
                ${steps}
            </div>`;

        if (type === 'fill_blank') return `
            ${open}${tools}
                <p class="ws-q-text"><span class="ws-q-n">${n}</span>${ed(`${qPath}.sentence`, q.sentence, '', 'Sentence with a ____ gap')}${keyChip(`${qPath}.answer`, q.answer, 'Answer')}</p>
                ${steps}
            </div>`;

        if (type === 'matching') {
            const pairs = q.pairs || [];
            const { order } = matchingLayout(q);
            // Drawing lines needs no numbers, letters or boxes - only a dot at
            // the inner edge of each item for the line to start and end on.
            const lines = !!so.lines;
            const dot = '<span class="ws-match-dot" aria-hidden="true"></span>';
            const pairTools = (i) => EDIT ? `<span class="ws-ed-pairtools" contenteditable="false">`
                + edBtn('pair-up', `${qPath}.pairs.${i}`, 'Move pair up', '&#9650;')
                + edBtn('pair-down', `${qPath}.pairs.${i}`, 'Move pair down', '&#9660;')
                + edBtn('pair-del', `${qPath}.pairs.${i}`, 'Remove pair', '&times;')
                + `</span>` : '';
            const left = pairs.map((p, i) =>
                `<div class="ws-match-item"${EDIT ? ` data-ws-pair="${escAttr(`${qPath}.pairs.${i}`)}"` : ''}>`
                + (EDIT ? edGrip('pair', `${qPath}.pairs.${i}`, 'Drag to move this pair') : '')
                + (lines ? '' : `<span class="ws-match-n">${i + 1}.</span> `)
                + `<span class="ws-match-text">${ed(`${qPath}.pairs.${i}.left`, p.left, '', 'Term')}</span>`
                + (lines ? dot : ' <span class="ws-match-box"></span>')
                + pairTools(i)
                + `</div>`
            ).join('');
            const LETTERS = 'ABCDEFGHIJ';
            const right = order.map((origIdx, pos) =>
                `<div class="ws-match-item">`
                + (lines ? dot : `<span class="ws-match-n">${LETTERS[pos] || pos + 1}.</span> `)
                + `<span class="ws-match-text">${ed(`${qPath}.pairs.${origIdx}.right`, pairs[origIdx].right, '', 'Definition')}</span></div>`
            ).join('');
            // Dropped when empty on the printed sheet, but always present in
            // edit mode: a teacher who clears the instruction would otherwise
            // have nowhere left to click to type a new one.
            const instr = (EDIT || q.instruction)
                ? `<p class="ws-q-text"><span class="ws-q-n">${n}</span>${ed(`${qPath}.instruction`, q.instruction, '', 'Instruction')}</p>`
                : '';
            return `
            ${open}${tools}
                ${instr}
                ${steps}
                <div class="ws-match-cols${lines ? ' ws-match-lines' : ''}">
                    <div class="ws-match-col">${left}</div>
                    <div class="ws-match-col">${right}</div>
                </div>
                ${EDIT ? `<div class="ws-ed-inline-add" contenteditable="false">${edBtn('pair-add', qPath, 'Add pair', '+ Add pair')}</div>` : ''}
            </div>`;
        }

        if (type === 'whiteboard') return `
            ${open}${tools}
                ${stemHtml(n, `${qPath}.question`, q.question)}
                ${steps}
                <div class="ws-whiteboard-box">${keyOn() ? `<div class="ws-ed-wbkey">${ed(`${qPath}.answer`, q.answer, 'ws-ed-keytext', 'Answer (teacher only)')}</div>` : ''}</div>
            </div>`;

        // Shown whenever there is something to show, and always in edit mode
        // so guidance can be added to a question that was generated without it.
        const guidance = (EDIT || q.guidance)
            ? `<p class="ws-guidance">${ed(`${qPath}.guidance`, q.guidance, '', 'Guidance (optional)')}</p>`
            : '';

        if (type === 'extended_response') {
            if (q.parts?.length) return `
            ${open}${tools}
                ${stemHtml(n, `${qPath}.question`, q.question)}
                ${guidance}
                ${questionDiagramHtml(q)}
                ${steps}
                ${partsHtml(q, qPath)}
            </div>`;
            const marks = questionMarks(type, q);
            const starter = starterHtml(q, qPath);
            // One line shorter when a starter is printed, so the answer space
            // stays the same height and the question does not grow a page.
            const lines = Math.min(18, Math.max(6, Math.round(marks * 1.5))) - (starter ? 1 : 0);
            return `
            ${open}${tools}
                ${stemHtml(n, `${qPath}.question`, q.question, ` ${marksHtml(qPath, marks)}`)}
                ${guidance}
                ${questionDiagramHtml(q)}
                ${steps}
                ${starter}
                ${answerSpace(lines, `${qPath}.modelAnswer`, q.modelAnswer)}
            </div>`;
        }

        // short_answer (default)
        if (q.parts?.length) return `
            ${open}${tools}
                ${stemHtml(n, `${qPath}.question`, q.question)}
                ${questionDiagramHtml(q)}
                ${steps}
                ${partsHtml(q, qPath)}
            </div>`;
        const marks = questionMarks('short_answer', q);
        const starter = starterHtml(q, qPath);
        return `
            ${open}${tools}
                ${stemHtml(n, `${qPath}.question`, q.question, q.marks || EDIT ? ' ' + marksHtml(qPath, marks) : '')}
                ${questionDiagramHtml(q)}
                ${steps}
                ${starter}
                ${answerSpace(starter ? 2 : 3, `${qPath}.answer`, q.answer)}
            </div>`;
    }

    // ── Worksheet body ────────────────────────────────────
    /**
     * @param {object} data { title, sections: [{type, questions}] }
     * @param {object} [meta] { subject, level, examBoard }
     * @param {object} [opts] { editable } - `editable` renders the same
     *        document with every model-backed string wrapped in a
     *        contenteditable span carrying its JSON path, plus the structural
     *        buttons the worksheet editor drives. Never set on the print,
     *        PDF or pupil-portal path, which is what keeps that output
     *        identical to what it was before the editor existed.
     */
    function buildWorksheetHtml(data, meta, opts) {
        if (!data) return '';
        meta = meta || {};
        // `answers`: the editor shows the sheet as the teacher's copy, with
        // every answer on it where it belongs and editable there. Off, it is
        // the pupil's sheet with only the questions editable.
        EDIT = (opts && opts.editable) ? { answers: opts.showAnswers !== false } : null;
        try {
            return worksheetBodyHtml(data, meta);
        } finally {
            // Cleared unconditionally: buildAnswerKey and any later print
            // render share this module, and a leaked flag would put
            // contenteditable spans into a PDF.
            EDIT = null;
        }
    }

    function worksheetBodyHtml(data, meta) {
        const sections = data.sections || [];
        const letters  = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
        // Read off the document, not off meta: the home language is part of
        // what the sheet IS, so a saved worksheet reopened months later still
        // prints its translated lines without the composer having to say so.
        const language = (HomeLanguages && HomeLanguages.cleanLanguage(data.language)) || '';

        const sectionsHtml = sections.map((section, si) => {
            const sPath  = `sections.${si}`;
            const letter = letters[si] || String(si + 1);
            const label  = section.title || WS_SECTION_LABELS[section.type] || section.type;
            const stock  = WS_PRINT_INSTRUCTION_OVERRIDES[section.type]
                || WS_SECTION_INSTRUCTIONS[section.type] || '';
            const lines  = matchByLines(section);
            const instr  = section.instruction || (lines ? MATCH_LINES_INSTRUCTION : stock);
            // The translated twin is a translation of the STOCK instruction.
            // Once a teacher has written their own, the two lines no longer
            // say the same thing, and a pupil reading the L1 line would be
            // following the instruction that was replaced - so it is dropped.
            // The same goes for matching by lines, which has its own wording.
            const l1 = section.instruction || lines
                ? ''
                : l1Line(language, HomeLanguages && HomeLanguages.sectionInstruction(language, section.type), 'ws-instruction-l1');
            // Shared steps are printed from the first question, which is also
            // where the editor writes them back to; it copies an edit made
            // here to the rest (wsKeepStepsShared in app.js).
            const lifted = sharedSteps(section) ? stepsHtml(section.questions[0], `${sPath}.questions.0`) : '';
            const qs = (section.questions || [])
                .map((q, qi) => renderQuestion(section.type, q, qi + 1, `${sPath}.questions.${qi}`, { stepsLifted: !!lifted, lines }))
                .join('');
            const bank = wordBankWords(section);
            const bankHtml = bank.length
                ? `<div class="ws-wordbank"><span class="ws-wordbank-label">Word bank</span>${bank.map(w => `<span class="ws-wordbank-word">${mathText(w)}</span>`).join('')}</div>`
                : '';
            // The layout choices this kind of section has, where there are any.
            const sectionOpts = !EDIT ? ''
                : section.type === 'fill_blank'
                    ? `<div class="ws-ed-opts" contenteditable="false">${edOption('s-wordbank', sPath, 'Word bank', !!section.wordBank)}</div>`
                : section.type === 'matching'
                    ? `<div class="ws-ed-opts" contenteditable="false">${edOption('s-matchstyle', sPath, 'Letters in boxes', !lines, 'letters')}${edOption('s-matchstyle', sPath, 'Draw lines', lines, 'lines')}</div>`
                : '';
            const sectionTools = EDIT ? `<div class="ws-ed-sectools" contenteditable="false">`
                + edBtn('s-up', sPath, 'Move section up', ICONS.up)
                + edBtn('s-down', sPath, 'Move section down', ICONS.down)
                + edBtn('s-del', sPath, 'Delete section', ICONS.bin)
                + `</div>` : '';
            const addQ = EDIT
                ? `<div class="ws-ed-inline-add ws-ed-addq" contenteditable="false">${edBtn('q-add', sPath, 'Add question', '+ Add question')}</div>`
                : '';
            return `
            <div class="ws-section ws-section--c${si % 4}${EDIT ? ' ws-ed-s' : ''}"${EDIT ? ` data-ws-s="${escAttr(sPath)}"` : ''}>
                <div class="ws-section-head">
                    ${sectionTools}
                    <span class="ws-section-letter">${letter}</span>
                    <div>
                        <p class="ws-section-title">${edPlain(`${sPath}.title`, label, '', 'Section name')}</p>
                        <p class="ws-instruction">${edPlain(`${sPath}.instruction`, instr, '', 'Instruction')}</p>
                        ${l1}
                    </div>
                </div>
                ${sectionOpts}
                ${workedExampleHtml(section, sPath)}
                ${lifted}
                ${bankHtml}
                ${qs}
                ${addQ}
            </div>`;
        }).join('');

        const chips = [meta.isQuiz ? 'Quiz' : null, meta.subject, meta.level, meta.examBoard]
            .filter(Boolean)
            .map(c => `<span class="ws-chip">${esc(c)}</span>`)
            .join('');

        const total = totalMarks(data);

        // The school's crest goes above the rule the header hangs from, not
        // inside the header: the title is what a pupil reads first and a logo
        // sharing that line competes with it. Empty for a free account and for
        // a paying teacher with no organisation – neither has a name to put
        // there. See utils/resourceMark.js.
        const brand = BrandMark ? BrandMark.headerHtml(meta.mark, 'ws') : '';

        // 'title' keeps the heading and drops the score box and the name and
        // date lines; 'none' drops the heading too. The editor always shows
        // the choice, or a header switched off could never be switched back.
        const header = headerMode(data);
        const headerPicker = EDIT ? `<div class="ws-ed-opts ws-ed-headopts" contenteditable="false">
                <span class="ws-ed-opts-label">Top of page</span>`
            + edOption('doc-header', 'header', 'Title, name and score', header === 'full', 'full')
            + edOption('doc-header', 'header', 'Title only', header === 'title', 'title')
            + edOption('doc-header', 'header', 'Nothing', header === 'none', 'none')
            + `</div>` : '';
        // Chosen from the Look gallery in the editor's toolbar, not on the
        // sheet: it is about the whole page, not one part of it.
        const theme = themeOf(data);
        // The QR code to the sheet's answers page, when the teacher asked for
        // one (utils/worksheetAnswers.js). An SVG our own server drew, so it
        // goes in as it is. Never in the editor: it is furniture of the
        // printed sheet, with nothing in it to edit. A homework sheet's code
        // only says whose it is (meta.qrIdOnly), so it does not offer answers;
        // nor does a student's own copy of a lesson's centre work (utils/printDay.js).
        const qr = meta.answersQr && !EDIT
            ? `<div class="ws-qr">${meta.answersQr}<span class="ws-qr-label">${meta.qrIdOnly ? (meta.homework ? 'Homework code' : 'Student code') : 'Scan for answers'}</span>${meta.studentName ? `<span class="ws-qr-name">${esc(meta.studentName)}</span>` : ''}</div>`
            : '';
        const headerHtml = header === 'none' ? (qr ? `<div class="ws-qr-solo">${qr}</div>` : '') : `
            <div class="ws-header">
                <div class="ws-header-main">
                    <p class="ws-doc-title">${edPlain('title', data.title, '', 'Worksheet title')}</p>
                    ${chips ? `<div class="ws-chips">${chips}</div>` : ''}
                </div>
                ${qr}
                ${header === 'full' ? `<div class="ws-score-box">
                    <span class="ws-score-label">Score</span>
                    <span class="ws-score-value">&nbsp;/ ${total}</span>
                </div>` : ''}
            </div>
            ${header === 'full' ? `<div class="ws-meta-row">
                <span class="ws-meta-field">Name:${l1Label(language, 'name')} <span class="ws-meta-line">${meta.studentName && !EDIT ? esc(meta.studentName) : ''}</span></span>
                <span class="ws-meta-field ws-meta-date">Date:${l1Label(language, 'date')} <span class="ws-meta-line"></span></span>
            </div>` : ''}`;

        const sheet = `
            ${brand}
            ${headerPicker}
            ${headerHtml}
            ${buildGlossary(data, language)}
            ${sectionsHtml}
            ${addSectionBar()}`;
        // Classic has no wrapper, so a sheet that never chose a look prints
        // exactly as it always did. The theme's rules hang off this element
        // rather than <body> because the portal and the PDF build their own.
        return theme === 'classic' ? sheet : `<div class="ws-sheet" data-ws-theme="${theme}">${sheet}</div>`;
    }

    // The six types a teacher can add by hand. `whiteboard` is deliberately
    // absent: it has no meaning on paper, and exists for the on-screen quiz.
    const WS_ADDABLE_TYPES = [
        'multiple_choice', 'true_false', 'fill_blank',
        'matching', 'short_answer', 'extended_response',
    ];

    function addSectionBar() {
        if (!EDIT) return '';
        const btns = WS_ADDABLE_TYPES
            .map(t => `<button type="button" class="ws-ed-btn ws-ed-typebtn" data-ws-act="s-add" data-ws-type="${t}">${esc(WS_SECTION_LABELS[t])}</button>`)
            .join('');
        return `<div class="ws-ed-addsection" contenteditable="false">
            <span class="ws-ed-addsection-label">Add a section</span>
            <div class="ws-ed-addsection-btns">${btns}</div>
        </div>`;
    }

    /**
     * The key-terms box, above the first question.
     *
     * Above rather than at the end, because a pupil who needs it needs it
     * before they hit the term, not after. Empty string when there is no
     * glossary, which is every worksheet that did not ask for one.
     */
    function buildGlossary(data, language) {
        const terms = (data && data.glossary) || [];
        if (!Array.isArray(terms) || !terms.length) return '';
        const lang = HomeLanguages && HomeLanguages.getLanguage(language);
        // Only when at least one term actually came back with a translation.
        // A column of empty cells reads as a mistake, and the model is told to
        // leave the field out rather than invent a word, so an all-blank
        // column is a normal outcome for very technical vocabulary.
        const bilingual = !!lang && terms.some(t => t && t.translation);
        // Print drops a half-written term; edit mode keeps every row, since a
        // term you have just added has neither word nor definition yet. The
        // path carries the index in the stored array either way.
        const rows = terms
            .map((t, i) => ({ t, i }))
            .filter(({ t }) => EDIT ? !!t : (t && t.term && t.definition))
            .map(({ t, i }) => `<div class="ws-gloss-item">
                    <dt class="ws-gloss-term">${ed(`glossary.${i}.term`, t.term, '', 'Term')}</dt>
                    <dd class="ws-gloss-def">${ed(`glossary.${i}.definition`, t.definition, '', 'Definition')}</dd>
                    ${bilingual ? `<dd class="ws-gloss-l1" lang="${lang.code}" dir="${lang.dir}">${edPlain(`glossary.${i}.translation`, t.translation || '', '', lang.endonym)}</dd>` : ''}
                    ${EDIT ? edBtn('gloss-del', `glossary.${i}`, 'Remove term', '&times;') : ''}
                </div>`)
            .join('');
        if (!rows) return '';
        const title = bilingual
            ? `Key words${l1Label(language, 'keyWords')}`
            : 'Key words';
        const add = EDIT
            ? `<div class="ws-ed-inline-add" contenteditable="false">${edBtn('gloss-add', 'glossary', 'Add term', '+ Add term')}</div>`
            : '';
        return `<div class="ws-glossary${bilingual ? ' ws-glossary-bilingual' : ''}">
            <p class="ws-gloss-title">${title}</p>
            <dl class="ws-gloss-list">${rows}</dl>
            ${add}
        </div>`;
    }

    // ── Answer key ────────────────────────────────────────

    function buildAnswerKey(data) {
        const sections = data.sections || [];
        const letters  = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];
        const parts    = [];

        sections.forEach((section, si) => {
            const letter = letters[si] || String(si + 1);
            // A section the teacher or a described sheet named is keyed by that
            // name, as on the sheet; otherwise by its type.
            const label  = section.title || WS_SECTION_LABELS[section.type] || section.type;
            const qs = section.questions || [];
            const heading = `<p class="ak-heading">Section ${letter} – ${esc(label)}</p>`;

            if (section.type === 'multiple_choice') {
                const ans = qs.map((q, qi) =>
                    `<span class="ak-item">${qi + 1}.&nbsp;${mathText((q.answer || '').toUpperCase())}</span>`
                ).join('');
                parts.push(`<div class="ak-section">${heading}<div class="ak-row">${ans}</div></div>`);
            } else if (section.type === 'true_false') {
                const ans = qs.map((q, qi) =>
                    `<span class="ak-item">${qi + 1}.&nbsp;${q.answer ? 'True' : 'False'}</span>`
                ).join('');
                parts.push(`<div class="ak-section">${heading}<div class="ak-row">${ans}</div></div>`);
            } else if (section.type === 'fill_blank') {
                const ans = qs.map((q, qi) =>
                    `<p class="ak-item ak-item-block">${qi + 1}.&nbsp;${mathText(q.answer)}</p>`
                ).join('');
                parts.push(`<div class="ak-section">${heading}${ans}</div>`);
            } else if (section.type === 'matching') {
                // Matched by drawing lines, the sheet has no numbers or letters
                // to quote, so the key gives each pair in words.
                const lines = matchByLines(section);
                const ans = qs.map(q => {
                    if (lines) {
                        return (q.pairs || []).map(p =>
                            `<p class="ak-item ak-item-block">${mathText(p.left)}&nbsp;&rarr;&nbsp;${mathText(p.right)}</p>`
                        ).join('');
                    }
                    const { letters: assigned } = matchingLayout(q);
                    return `<div class="ak-row">${(q.pairs || []).map((p, i) =>
                        `<span class="ak-item">${i + 1}&nbsp;–&nbsp;${assigned[i]}</span>`
                    ).join('')}</div>`;
                }).join('');
                parts.push(`<div class="ak-section">${heading}${ans}</div>`);
            } else if (section.type === 'short_answer') {
                const ans = qs.map((q, qi) => withKeyDiagram(q.parts?.length
                    ? partsAnswerHtml(qi + 1, q)
                    : (q.answer
                        ? `<div class="ak-indicative"><p class="ak-ind-q">${qi + 1}. ${mathText(q.question)}</p><p class="ak-ind-a">${mathText(q.answer)}</p></div>`
                        : ''), q, qi + 1)).join('');
                if (ans.trim()) parts.push(`<div class="ak-section">${heading}${ans}</div>`);
            } else if (section.type === 'whiteboard') {
                const ans = qs.map((q, qi) => q.answer
                    ? `<div class="ak-indicative"><p class="ak-ind-q">${qi + 1}. ${mathText(q.question)}</p><p class="ak-ind-label">Indicative answer:</p><p class="ak-ind-a">${mathText(q.answer)}</p></div>`
                    : ''
                ).join('');
                if (ans.trim()) parts.push(`<div class="ak-section">${heading}${ans}</div>`);
            } else if (section.type === 'extended_response') {
                const ans = qs.map((q, qi) => withKeyDiagram(q.parts?.length
                    ? partsAnswerHtml(qi + 1, q)
                    : (q.modelAnswer
                        ? `<div class="ak-indicative"><p class="ak-ind-q">${qi + 1}. ${mathText(q.question)} <span class="ak-marks">[${questionMarks('extended_response', q)} marks]</span></p><p class="ak-ind-label">Indicative content:</p><p class="ak-ind-a">${mathText(q.modelAnswer)}</p></div>`
                        : ''), q, qi + 1)).join('');
                if (ans.trim()) parts.push(`<div class="ak-section">${heading}${ans}</div>`);
            }
        });

        if (!parts.length) return '';
        return `<div class="ak-page"><p class="ak-title">Answer Key</p>${parts.join('')}</div>`;
    }

    // ── Full document ─────────────────────────────────────

    /**
     * Build the complete printable HTML document.
     * @param {object} data
     * @param {boolean} withAnswers
     * @param {object} opts { meta, css (inline text), cssHref (link URL),
     *   copies: [{ studentName, answersQr }] for one sheet per student, each
     *   starting a page of its own, with the answer key once after the last }
     */
    function buildPrintPage(data, withAnswers, opts) {
        opts = opts || {};
        const copies    = !opts.editable && Array.isArray(opts.copies) && opts.copies.length ? opts.copies : null;
        const body      = copies
            ? copies.map(c => `<div class="ws-copy">${buildWorksheetHtml(data, Object.assign({}, opts.meta, c), opts)}</div>`).join('')
            : buildWorksheetHtml(data, opts.meta, opts);
        // Never with answers: the editor is the student copy, and the answer
        // key has no editable projection - answers are edited in the inspector.
        const answerKey = withAnswers && !opts.editable ? buildAnswerKey(data) : '';
        // Two stylesheets when linking, because the OpenDyslexic faces live in
        // their own file now. Fonts first – @font-face must be declared before
        // the layout rule that names the family. Callers that inline (the PDF
        // path, the print popup) join the two themselves and pass one string.
        const styleTag  = opts.css
            ? `<style>${opts.css}</style>`
            : `<link rel="stylesheet" href="/vendor/katex/katex.min.css">`
              + `<link rel="stylesheet" href="${esc(opts.fontsHref || '/css/a11y-fonts.css')}">`
              + `<link rel="stylesheet" href="/css/worksheet-fonts.css">`
              + `<link rel="stylesheet" href="${esc(opts.cssHref || '/css/worksheet-print.css')}">`;
        // Last, so it wins over the print rules it decorates. Editor chrome
        // only - the print popup and the PDF never link this.
        const editStyle = opts.editable
            ? `<link rel="stylesheet" href="${esc(opts.editCssHref || '/css/worksheet-edit.css')}">`
            : '';
        // Both attributes are validated against public/js/a11y-profiles.js
        // before they get here, and escaped again on the way out – they land in
        // an attribute, so a raw query parameter would be an injection.
        const a11y      = opts.meta && opts.meta.a11y;
        const tint      = opts.meta && opts.meta.tint;
        const bodyAttrs = (a11y ? ` data-a11y="${esc(a11y)}"` : '')
                        + (tint ? ` data-tint="${esc(tint)}"` : '');
        const bodyTag   = `<body${bodyAttrs}>`;
        // After the answer key rather than at the end of the worksheet body:
        // with ?answers=1 the key is the last thing in the teacher's hand, and
        // a credit line stranded above it reads as the end of a document that
        // then carries on for two more pages.
        const credit    = BrandMark ? BrandMark.creditHtml(opts.meta && opts.meta.mark, 'ws') : '';
        return `<!DOCTYPE html><html><head><meta charset="utf-8">
<title>${esc((data && data.title) || (opts.meta && opts.meta.isQuiz ? 'Quiz' : 'Worksheet'))}</title>
${styleTag}${editStyle}
</head>${bodyTag}${body}${answerKey}${credit}</body></html>`;
    }

    return {
        tableText,
        WS_SECTION_LABELS,
        WS_SECTION_INSTRUCTIONS,
        WS_PRINT_INSTRUCTION_OVERRIDES,
        MATCH_LINES_INSTRUCTION,
        headerMode,
        WS_THEMES,
        WS_THEME_INFO,
        themeOf,
        matchByLines,
        questionDiagramHtml,
        partDiagramsHtml,
        partKeyDiagramsHtml,
        renderCurveDiagramSvg,
        esc,
        questionMarks,
        totalMarks,
        matchingLayout,
        sharedSteps,
        wordBankWords,
        buildWorksheetHtml,
        buildAnswerKey,
        buildPrintPage,
    };
}));
